/**
 * Web Device Detection Utilities
 * Browser-based device information detection for web platforms
 */

/**
 * Parse browser information from user agent
 * @returns {Object} Browser name and version
 */
export const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  
  // Chrome
  if (ua.includes('Chrome') && !ua.includes('Edg')) {
    const match = ua.match(/Chrome\/(\d+\.\d+)/);
    return {
      name: 'Chrome',
      version: match ? match[1] : 'Unknown'
    };
  }
  
  // Firefox
  if (ua.includes('Firefox')) {
    const match = ua.match(/Firefox\/(\d+\.\d+)/);
    return {
      name: 'Firefox',
      version: match ? match[1] : 'Unknown'
    };
  }
  
  // Safari
  if (ua.includes('Safari') && !ua.includes('Chrome')) {
    const match = ua.match(/Version\/(\d+\.\d+)/);
    return {
      name: 'Safari',
      version: match ? match[1] : 'Unknown'
    };
  }
  
  // Edge
  if (ua.includes('Edg')) {
    const match = ua.match(/Edg\/(\d+\.\d+)/);
    return {
      name: 'Edge',
      version: match ? match[1] : 'Unknown'
    };
  }
  
  // Default
  return {
    name: 'Unknown Browser',
    version: 'Unknown'
  };
};

/**
 * Parse operating system information from user agent
 * @returns {Object} OS name and version
 */
export const getOSInfo = () => {
  const ua = navigator.userAgent;
  
  // Windows
  if (ua.includes('Windows')) {
    if (ua.includes('Windows NT 10.0')) {
      return { name: 'Windows', version: '10' };
    } else if (ua.includes('Windows NT 6.3')) {
      return { name: 'Windows', version: '8.1' };
    } else if (ua.includes('Windows NT 6.2')) {
      return { name: 'Windows', version: '8' };
    } else if (ua.includes('Windows NT 6.1')) {
      return { name: 'Windows', version: '7' };
    }
    return { name: 'Windows', version: 'Unknown' };
  }
  
  // macOS
  if (ua.includes('Mac OS X')) {
    const match = ua.match(/Mac OS X (\d+[._]\d+)/);
    return {
      name: 'macOS',
      version: match ? match[1].replace('_', '.') : 'Unknown'
    };
  }
  
  // iOS
  if (ua.includes('iPhone') || ua.includes('iPad')) {
    const match = ua.match(/OS (\d+[._]\d+)/);
    return {
      name: 'iOS',
      version: match ? match[1].replace('_', '.') : 'Unknown'
    };
  }
  
  // Android
  if (ua.includes('Android')) {
    const match = ua.match(/Android (\d+\.\d+)/);
    return {
      name: 'Android',
      version: match ? match[1] : 'Unknown'
    };
  }
  
  // Linux
  if (ua.includes('Linux')) {
    return { name: 'Linux', version: 'Unknown' };
  }
  
  return { name: 'Unknown OS', version: 'Unknown' };
};

/**
 * Generate a unique browser fingerprint
 * @returns {string} Unique fingerprint string
 */
export const getDeviceFingerprint = () => {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.platform,
    navigator.cookieEnabled ? 'cookies' : 'no-cookies',
    navigator.doNotTrack || 'unknown'
  ];
  
  // Simple hash function
  let hash = 0;
  const str = components.join('|');
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(36);
};

/**
 * Get screen information
 * @returns {Object} Screen dimensions
 */
export const getScreenInfo = () => {
  return {
    width: screen.width,
    height: screen.height,
    colorDepth: screen.colorDepth,
    pixelRatio: window.devicePixelRatio || 1
  };
};

/**
 * Get comprehensive web device information
 * @returns {Object} Complete device info for web platform
 */
export const getWebDeviceInfo = () => {
  const browser = getBrowserInfo();
  const os = getOSInfo();
  const screen = getScreenInfo();
  const fingerprint = getDeviceFingerprint();
  
  return {
    deviceId: fingerprint,
    platform: 'web',
    model: 'Browser',
    manufacturer: browser.name,
    operatingSystem: os.name,
    osVersion: os.version,
    browserName: browser.name,
    browserVersion: browser.version,
    screenWidth: screen.width,
    screenHeight: screen.height,
    colorDepth: screen.colorDepth,
    pixelRatio: screen.pixelRatio,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookieEnabled: navigator.cookieEnabled,
    online: navigator.onLine
  };
};
